import React, { useState, useEffect } from 'react';
import { Upload, Send, Edit2, ArrowLeft, PlusCircle, CheckCircle, FileText, Eye, X } from 'lucide-react';
import apiService from '../config';
import { useLanguage } from '../StoreContext/LanguageContext';
import SignaturePad from '../DigitalSignature/SignaturePad';

const ROLES = {
    LAO: 3,
    PROJECT_INCHARGE: 18
};

const FundRequestReceived = () => {
    const { language, translations } = useLanguage();
    const userData = JSON.parse(localStorage.getItem('userData'));
    const userRole = parseInt(userData?.data?.user_role || userData?.user_role, 10);

    // Core Data States
    const [currentProjectId, setCurrentProjectId] = useState(null);
    const [proposalId, setProposalId] = useState(null);
    const [section11FundData, setSection11FundData] = useState([]);
    const [section19FundData, setSection19FundData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedSectionType, setSelectedSectionType] = useState(null);

    // --- Digital Signature States ---
    const [showSignaturePad, setShowSignaturePad] = useState(false);
    const [pdfForSigning, setPdfForSigning] = useState(null);
    const [uploadStatus, setUploadStatus] = useState('idle'); // 'idle' | 'uploading' | 'success' | 'error'
    const [uploadError, setUploadError] = useState('');
    const [selectedSignDocument, setSelectedSignDocument] = useState(null);
      const [showDocumentViewer, setShowDocumentViewer] = useState(false); // Unified document viewer modal
        const [fileUrlToView, setFileUrlToView] = useState('');
        const [isPDFToView, setIsPDFToView] = useState(false);

    // Reset Signature State function
    const resetSignatureState = () => {
        setUploadStatus('idle');
        setUploadError('');
        setSelectedSignDocument(null);
        setPdfForSigning(null);
    };
      const closeDocumentViewer = () => {
        setShowDocumentViewer(false);
        // Revoke Object URL if it was created from a File object
        if (fileUrlToView.startsWith('blob:')) {
            URL.revokeObjectURL(fileUrlToView);
        }
        setFileUrlToView('');
        setIsPDFToView(false);
    };


    // View Document Handler
    const handleViewDocument = (urlOrFile) => {
        if (!urlOrFile) return;

        let url;
        let isPdf;

        if (urlOrFile instanceof File) {
            url = URL.createObjectURL(urlOrFile);
            isPdf = urlOrFile.type === 'application/pdf';
        } else { // Assuming it's a URL string from backend
            url = urlOrFile;
            isPdf = urlOrFile.toLowerCase().includes('.pdf');
        }

        setFileUrlToView(url);
        setIsPDFToView(isPdf);
        setShowDocumentViewer(true);
    };
    // Mock/Hook function for signing (Replace with your actual digital sign logic if coming from a custom hook)
    const signDocument = async (file, signaturesArray) => {
        setUploadStatus('uploading');
        try {
            // Simulated signing delay - Replace with actual API/Signing logic
            await new Promise(resolve => setTimeout(resolve, 1500));
            setSelectedSignDocument(file); // Assume the file is signed and stored here
            setUploadStatus('success');
        } catch (err) {
            setUploadError('Failed to sign document');
            setUploadStatus('error');
        }
    };

    // LAO Role States (Requesting Funds)
    const [showLAORequestModal, setShowLAORequestModal] = useState(false);
    const [usersProjectInchargeAll, setUsersProjectInchargeAll] = useState([]);
    const [loadingUsersPI, setLoadingUsersPI] = useState(false);
    const [laoFormData, setLaoFormData] = useState({
        requestedFund: '',
        requestDate: '',
        selectedUser: '',
        remarks: 'Approved',
        document: null
    });
    const [isSubmittingLAO, setIsSubmittingLAO] = useState(false);

    // Project Incharge Role States (Sending/Approving Funds)
    const [showPIApproveModal, setShowPIApproveModal] = useState(false);
    const [selectedFundId, setSelectedFundId] = useState(null);
    const [usersLAO, setUsersLAO] = useState([]);
    const [loadingUsersLAO, setLoadingUsersLAO] = useState(false);
    const [piFormData, setPiFormData] = useState({
        receivedFund: '',
        receiveDate: '',
        chequeDate: '',
        chequeNo: '',
        chequeAmount: '',
        selectedUser: '',
        remarks: 'Approved',
        document: null
    });
    const [isSubmittingPI, setIsSubmittingPI] = useState(false);

    // Initialization Effect
    useEffect(() => {
        const getProjectInfo = () => {
            const selectedProject = localStorage.getItem('selectedProject');
            if (selectedProject) {
                try {
                    const project = JSON.parse(selectedProject);
                    return { id: project.project_id || project.id, name: project.project_name || project.name };
                } catch (e) {
                    console.error('Error parsing selected project:', e);
                }
            }
            if (userData) {
                const projectId = userData.data?.project_id || userData.project_id;
                return { id: projectId, name: 'Unknown Project' };
            }
            return { id: null, name: null };
        };

        const projectInfo = getProjectInfo();
        setCurrentProjectId(projectInfo.id);
        const savedProposalId = localStorage.getItem('currentProposalId');
        if (savedProposalId) setProposalId(savedProposalId);
    }, []);

    // Fetch Data Effect
    useEffect(() => {
        if (currentProjectId && proposalId) {
            fetchFundData();
            fetchDropdownUsers();
        }
    }, [currentProjectId, proposalId]);

    const fetchFundData = async () => {
        setLoading(true);
        try {
            const [response11, response19] = await Promise.all([
                apiService.getFundRequestReceivedSection11(currentProjectId, proposalId),
                apiService.getFundRequestReceivedSection19(currentProjectId, proposalId),
            ]);
            setSection11FundData(response11.data || []);
            setSection19FundData(response19.data || []);
        } catch (error) {
            console.error('Error fetching fund data:', error);
            alert('Failed to fetch fund data');
        } finally {
            setLoading(false);
        }
    };

    const fetchDropdownUsers = async () => {
        try {
            setLoadingUsersPI(true);
            setLoadingUsersLAO(true);
            const [piResponse, laoResponse] = await Promise.all([
                apiService.getAssignUserRoleProjectIncharge(ROLES.PROJECT_INCHARGE, currentProjectId, proposalId),
                apiService.getAssignLAO(ROLES.LAO, currentProjectId, proposalId)
            ]);
            setUsersProjectInchargeAll(piResponse.data || []);
            setUsersLAO(laoResponse.data || []);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoadingUsersPI(false);
            setLoadingUsersLAO(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
        } catch {
            return dateString;
        }
    };

    // --- LAO Action: Submit Fund Request ---
    const handleLAOSubmitRequest = async () => {
        const { requestedFund, requestDate, selectedUser, remarks, document } = laoFormData;
        if (!selectedUser || !requestedFund || !requestDate) {
            return alert('Please fill in all required fields');
        }

        setIsSubmittingLAO(true);
        try {
            // Use signed document if it exists, otherwise fall back to original uploaded document
            const finalDoc = (uploadStatus === 'success' && selectedSignDocument) ? selectedSignDocument : document;

            const fundData = new FormData();
            fundData.append('project_id', currentProjectId);
            fundData.append('proposal_id', proposalId);
            fundData.append('requested_fund', requestedFund);
            fundData.append('received_fund', '0');
            fundData.append('date', requestDate);
            fundData.append('type', selectedSectionType);
            if (finalDoc) fundData.append('document', finalDoc);

            if (selectedSectionType === 'section11') {
                await apiService.postAddFundRequestReceivedSection11(fundData);
            } else {
                await apiService.postAddFundRequestReceivedSection19(fundData);
            }

            const trackingData = new FormData();
            trackingData.append('project_id', currentProjectId);
            trackingData.append('proposal_id', proposalId);
            trackingData.append('usr_id', selectedUser);
            trackingData.append('remarks', remarks);
            if (finalDoc) trackingData.append('document', finalDoc);

            const trackingName = selectedSectionType === 'section11' ? 'ProjectInchargeFundSection11' : 'ProjectInchargeFundSection19';
            await apiService.postTrackingSendToProjectIncharge(trackingData, trackingName);

            alert(`Successfully sent request to Project Incharge!`);
            setShowLAORequestModal(false);
            setLaoFormData({ requestedFund: '', requestDate: '', selectedUser: '', remarks: 'Approved', document: null });
            resetSignatureState();
            fetchFundData();
        } catch (error) {
            console.error('Error sending request:', error);
            alert('Failed to send request');
        } finally {
            setIsSubmittingLAO(false);
        }
    };

    // --- PI Action: Approve/Send Fund ---
    const handlePIApproveFund = async () => {
        const { receivedFund, receiveDate, chequeDate, chequeNo, chequeAmount, selectedUser, remarks, document } = piFormData;
        if (!selectedUser || !receivedFund || !receiveDate) {
            return alert('Please fill in all required fields');
        }

        const currentData = selectedSectionType === 'section11' ? section11FundData : section19FundData;
        const currentFund = currentData.find(f => f.fund_id === selectedFundId);
        
        if (parseFloat(receivedFund) > parseFloat(currentFund?.requested_fund || 0)) {
            return alert(`Received Fund (₹${receivedFund}) cannot be greater than Requested Fund (₹${currentFund?.requested_fund})!`);
        }

        setIsSubmittingPI(true);
        try {
            // Use signed document if it exists, otherwise fall back to original uploaded document
            const finalDoc = (uploadStatus === 'success' && selectedSignDocument) ? selectedSignDocument : document;

            const updateData = new FormData();
            updateData.append('received_fund', receivedFund);
            updateData.append('receive_date', receiveDate);
            if (chequeDate) updateData.append('cheque_date', chequeDate);
            if (chequeNo) updateData.append('cheque_no', chequeNo); 
            if (chequeAmount) updateData.append('cheque_amount', chequeAmount);
            if (finalDoc) updateData.append('document', finalDoc);

            await apiService.patchUpdateFundRequestReceived(selectedFundId, updateData);

            const trackingData = new FormData();
            trackingData.append('project_id', currentProjectId);
            trackingData.append('proposal_id', proposalId);
            trackingData.append('usr_id', selectedUser);
            trackingData.append('remarks', remarks);
            if (finalDoc) trackingData.append('document', finalDoc);

            if (selectedSectionType === 'section11') {
                await apiService.postTrackingFundSendToLAOSection11(trackingData, 'LAOFundSection11');
            } else {
                await apiService.postTrackingFundSendToLAOSection19(trackingData, 'LAOFundSection19');
            }

            alert('Successfully sent fund details to LAO!');
            setShowPIApproveModal(false);
            setPiFormData({ receivedFund: '', receiveDate: '', chequeDate: '', chequeNo: '', chequeAmount: '', selectedUser: '', remarks: 'Approved', document: null });
            resetSignatureState();
            fetchFundData();
        } catch (error) {
            console.error('Error sending fund details:', error);
            alert('Failed to send back to LAO');
        } finally {
            setIsSubmittingPI(false);
        }
    };

    // --- Reusable UI Components ---
    const SectionHeader = ({ title, sectionType }) => (
        <div className="bg-white rounded-t-lg shadow-sm p-5 border-b border-gray-100 flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <FileText className="text-blue-600" size={24} />
                {title}
            </h2>
            {userRole === ROLES.LAO && (
                <button
                    onClick={() => {
                        setSelectedSectionType(sectionType);
                        setShowLAORequestModal(true);
                    }}
                    className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-all shadow-sm"
                >
                    <PlusCircle size={18} />
                    {translations[language]?.RequestFund || 'Request Fund'}
                </button>
            )}
        </div>
    );

    const FundTable = ({ data, sectionType }) => (
        <div className="bg-white rounded-b-lg shadow-md overflow-hidden mb-8">
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Request Date</th>
                            <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Requested Fund</th>
                            <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Received Fund</th>
                            <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Cheque Date</th>
                            <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Cheque No</th>
                            <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Cheque Amount</th>
                            <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Receive Date</th>
                            {userRole === ROLES.PROJECT_INCHARGE && (
                                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Action</th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading && data.length === 0 ? (
                            <tr><td colSpan="8" className="px-6 py-8 text-center text-gray-500 animate-pulse">Loading data...</td></tr>
                        ) : data.length === 0 ? (
                            <tr><td colSpan="8" className="px-6 py-8 text-center text-gray-500 bg-gray-50">No requests have been made yet.</td></tr>
                        ) : (
                            data.map((fund) => (
                                <tr key={fund.fund_id} className="hover:bg-blue-50/50 transition-colors">
                                    <td className="px-6 py-4 text-left text-sm text-gray-900">{formatDate(fund.date)}</td>
                                    <td className="px-6 py-4 text-right text-sm font-medium text-blue-600">₹{fund.requested_fund?.toLocaleString() || '0'}</td>
                                    <td className="px-6 py-4 text-right text-sm font-medium text-green-600">₹{fund.received_fund?.toLocaleString() || '0'}</td>
                                    <td className="px-6 py-4 text-center text-sm text-gray-600">{formatDate(fund.cheque_date)}</td>
                                    <td className="px-6 py-4 text-center text-sm text-gray-900">{fund.cheque_no || '-'}</td>
                                    <td className="px-6 py-4 text-right text-sm text-gray-900">{fund.cheque_amount ? `₹${fund.cheque_amount.toLocaleString()}` : '-'}</td>
                                    <td className="px-6 py-4 text-center text-sm text-gray-600">{formatDate(fund.receive_date)}</td>
                                    {userRole === ROLES.PROJECT_INCHARGE && (
                                        <td className="px-6 py-4 text-center">
                                            <button
                                                onClick={() => {
                                                    setSelectedSectionType(sectionType);
                                                    setSelectedFundId(fund.fund_id);
                                                    setPiFormData(prev => ({ 
                                                        ...prev, 
                                                        receivedFund: fund.received_fund || '', 
                                                        receiveDate: fund.receive_date ? fund.receive_date.split('T')[0] : '',
                                                        chequeDate: fund.cheque_date ? fund.cheque_date.split('T')[0] : '',
                                                        chequeNo: fund.cheque_no || '',
                                                        chequeAmount: fund.cheque_amount || ''
                                                    }));
                                                    setShowPIApproveModal(true);
                                                }}
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                                            >
                                                <CheckCircle size={16} /> Send Funds
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    if (!currentProjectId || !proposalId) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="bg-white p-8 rounded-xl shadow-sm text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading project context...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Fund Management</h1>
                <p className="text-gray-500">Manage fund requests and allocations between LAO and Project Incharge.</p>
            </div>

            {/* Section 11 Block */}
            <SectionHeader title="कलम 19 ची घोषणा करिता आवश्यक निधी मागणी" sectionType="section11" />
            <FundTable data={section11FundData} sectionType="section11" />

            {/* Section 19 Block */}
            <SectionHeader title="अंतिमनिवाडा करिता आवश्यक निधी मागणी" sectionType="section19" />
            <FundTable data={section19FundData} sectionType="section19" />

            {/* MODAL: LAO Requesting Funds */}
            {showLAORequestModal && (
                <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 flex-shrink-0">
                            <h2 className="text-lg font-bold text-gray-800">Request Funds</h2>
                            <button
                                onClick={() => {
                                    setShowLAORequestModal(false);
                                    resetSignatureState();
                                    setLaoFormData({ requestedFund: '', requestDate: '', selectedUser: '', remarks: 'Approved', document: null });
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                        <div className="p-6 space-y-4 overflow-y-auto">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Requested Fund (₹)*</label>
                                <input type="number" value={laoFormData.requestedFund} onChange={(e) => setLaoFormData({ ...laoFormData, requestedFund: e.target.value })} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g. 500000" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Request Date*</label>
                                <input type="date" value={laoFormData.requestDate} onChange={(e) => setLaoFormData({ ...laoFormData, requestDate: e.target.value })} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Select Project Incharge*</label>
                                <select value={laoFormData.selectedUser} onChange={(e) => setLaoFormData({ ...laoFormData, selectedUser: e.target.value })} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                                    <option value="">{loadingUsersPI ? 'Loading...' : 'Select User'}</option>
                                    {usersProjectInchargeAll.map((u) => <option key={u.usr_id} value={u.usr_id}>{u.full_name} ({u.user_role || u.usr_role})</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                                <textarea value={laoFormData.remarks} onChange={(e) => setLaoFormData({ ...laoFormData, remarks: e.target.value })} rows={2} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                            </div>
                            
                            {/* Rich Document Upload (LAO) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Document (Optional)
                                </label>
                                <input
                                    type="file"
                                    onChange={(e) => {
                                        setLaoFormData({ ...laoFormData, document: e.target.files[0] });
                                        resetSignatureState();
                                    }}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                />
                                {laoFormData.document && (
                                    <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-sm text-blue-700 font-medium truncate max-w-[60%]">
                                                Selected: {laoFormData.document.name}
                                            </p>
                                            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                                                {(laoFormData.document.size / 1024 / 1024).toFixed(2)} MB
                                            </span>
                                        </div>
                                        
                                        <div className="flex gap-2">
                                            {laoFormData.document.type === 'application/pdf' && (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (laoFormData.document?.type === 'application/pdf') {
                                                                setPdfForSigning(laoFormData.document);
                                                                setShowSignaturePad(true);
                                                            }
                                                        }}
                                                        disabled={uploadStatus === 'uploading'}
                                                        className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
                                                    >
                                                        {uploadStatus === 'uploading' ? (
                                                            <>
                                                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                                                Signing...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                                </svg>
                                                                Sign Doc
                                                            </>
                                                        )}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleViewDocument(
                                                            uploadStatus === 'success' ? selectedSignDocument : laoFormData.document
                                                        )}
                                                        className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1 transition-colors"
                                                    >
                                                        <Eye className="h-3 w-3" />
                                                        View
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                        
                                        {/* Upload status messages */}
                                        {uploadStatus === 'success' && (
                                            <div className="mt-2 p-2 bg-green-100 border border-green-300 rounded text-xs text-green-700">
                                                ✅ Document signed successfully!
                                            </div>
                                        )}
                                        {uploadStatus === 'error' && uploadError && (
                                            <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-xs text-red-700">
                                                ❌ {uploadError}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                        </div>
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 flex-shrink-0">
                            <button onClick={() => {
                                setShowLAORequestModal(false);
                                resetSignatureState();
                                setLaoFormData({ requestedFund: '', requestDate: '', selectedUser: '', remarks: 'Approved', document: null });
                            }} className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-200 rounded-lg transition-colors">Cancel</button>
                            <button onClick={handleLAOSubmitRequest} disabled={isSubmittingLAO} className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2">
                                {isSubmittingLAO ? 'Sending...' : <><Send size={16} /> Send Request</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: PI Approving/Sending Funds */}
            {showPIApproveModal && (
                <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center flex-shrink-0">
                            <h2 className="text-lg font-bold text-gray-800">Process & Allocate Funds</h2>
                            <button
                                onClick={() => {
                                    setShowPIApproveModal(false);
                                    resetSignatureState();
                                    setPiFormData({ receivedFund: '', receiveDate: '', chequeDate: '', chequeNo: '', chequeAmount: '', selectedUser: '', remarks: 'Approved', document: null });
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                        <div className="p-6 space-y-4 overflow-y-auto">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Fund (₹)*</label>
                                    <input type="number" value={piFormData.receivedFund} onChange={(e) => setPiFormData({ ...piFormData, receivedFund: e.target.value })} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date*</label>
                                    <input type="date" value={piFormData.receiveDate} onChange={(e) => setPiFormData({ ...piFormData, receiveDate: e.target.value })} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Cheque Date</label>
                                    <input type="date" value={piFormData.chequeDate} onChange={(e) => setPiFormData({ ...piFormData, chequeDate: e.target.value })} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Cheque No</label>
                                    <input type="text" value={piFormData.chequeNo} onChange={(e) => setPiFormData({ ...piFormData, chequeNo: e.target.value })} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Cheque Amount</label>
                                    <input type="number" value={piFormData.chequeAmount} onChange={(e) => setPiFormData({ ...piFormData, chequeAmount: e.target.value })} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Select LAO Officer*</label>
                                <select value={piFormData.selectedUser} onChange={(e) => setPiFormData({ ...piFormData, selectedUser: e.target.value })} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                                    <option value="">{loadingUsersLAO ? 'Loading...' : 'Select User'}</option>
                                    {usersLAO.map((u) => <option key={u.id || u.usr_id} value={u.id || u.usr_id}>{u.full_name || u.user_name} ({u.usr_role || u.user_role})</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                                <textarea value={piFormData.remarks} onChange={(e) => setPiFormData({ ...piFormData, remarks: e.target.value })} rows={2} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                            </div>

                            {/* Rich Document Upload (PI) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Document (Optional)
                                </label>
                                <input
                                    type="file"
                                    onChange={(e) => {
                                        setPiFormData({ ...piFormData, document: e.target.files[0] });
                                        resetSignatureState();
                                    }}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                />
                                {piFormData.document && (
                                    <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-sm text-green-700 font-medium truncate max-w-[60%]">
                                                Selected: {piFormData.document.name}
                                            </p>
                                            <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                                {(piFormData.document.size / 1024 / 1024).toFixed(2)} MB
                                            </span>
                                        </div>
                                        
                                        <div className="flex gap-2">
                                            {piFormData.document.type === 'application/pdf' && (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (piFormData.document?.type === 'application/pdf') {
                                                                setPdfForSigning(piFormData.document);
                                                                setShowSignaturePad(true);
                                                            }
                                                        }}
                                                        disabled={uploadStatus === 'uploading'}
                                                        className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
                                                    >
                                                        {uploadStatus === 'uploading' ? (
                                                            <>
                                                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                                                Signing...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                                </svg>
                                                                Sign Doc
                                                            </>
                                                        )}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleViewDocument(
                                                            uploadStatus === 'success' ? selectedSignDocument : piFormData.document
                                                        )}
                                                        className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1 transition-colors"
                                                    >
                                                        <Eye className="h-3 w-3" />
                                                        View
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                        
                                        {/* Upload status messages */}
                                        {uploadStatus === 'success' && (
                                            <div className="mt-2 p-2 bg-green-100 border border-green-300 rounded text-xs text-green-700">
                                                ✅ Document signed successfully!
                                            </div>
                                        )}
                                        {uploadStatus === 'error' && uploadError && (
                                            <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-xs text-red-700">
                                                ❌ {uploadError}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 flex-shrink-0">
                            <button onClick={() => {
                                setShowPIApproveModal(false);
                                resetSignatureState();
                                setPiFormData({ receivedFund: '', receiveDate: '', chequeDate: '', chequeNo: '', chequeAmount: '', selectedUser: '', remarks: 'Approved', document: null });
                            }} className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-200 rounded-lg transition-colors">Cancel</button>
                            <button onClick={handlePIApproveFund} disabled={isSubmittingPI} className="px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center gap-2">
                                {isSubmittingPI ? 'Processing...' : <><CheckCircle size={16} /> Allocate Funds</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* SignaturePad Modal Wrapper */}
            {showSignaturePad && pdfForSigning && (
                <SignaturePad
                    pdfFile={pdfForSigning}
                    onSign={(signaturesArray) => {
                        setShowSignaturePad(false);
                        signDocument(pdfForSigning, signaturesArray).then(() => {
                            setPdfForSigning(null);
                        });
                    }}
                    onCancel={() => {
                        setShowSignaturePad(false);
                        setPdfForSigning(null);
                    }}
                />
            )}
             {showDocumentViewer && (
                    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-3xl w-full max-w-5xl h-[95vh] shadow-2xl overflow-hidden border border-gray-200">
                            {/* Header */}
                            <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                                <div className="flex items-center space-x-3">
                                    <h3 className="text-2xl font-bold text-gray-800 tracking-tight">Document Preview</h3>
                                </div>
                                <button
                                    onClick={closeDocumentViewer}
                                    className="w-10 h-10 rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-500 transition-all duration-200 flex items-center justify-center text-xl font-bold shadow-sm hover:shadow-md"
                                    aria-label="Close modal"
                                >
                                    ×
                                </button>
                            </div>

                            {/* Document Viewer Container */}
                            <div className="h-[calc(95vh)] flex flex-col">
                                <div className="flex-1 bg-gray-50 overflow-hidden shadow-inner border border-gray-200">
                                    {/* Document Content */}
                                    <div className="w-full h-full flex items-center justify-center">
                                        {isPDFToView ? (
                                            <iframe
                                                src={fileUrlToView}
                                                className="w-full h-full border-0 bg-white shadow-lg"
                                                title="PDF Preview"
                                            ></iframe>
                                        ) : (
                                            <img
                                                src={fileUrlToView}
                                                alt="File"
                                                className="w-full h-full object-contain shadow-lg"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
        </div>
    );
};

export default FundRequestReceived;
